import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { Drawer } from 'antd'
import useI18n from '../../i18n/useI18n'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { ReactComponent as TurkeyMapSvg } from './turkey-map.svg'
import './index.scss'

const decodeLabel = (value = '') =>
  value
    .replace(/\\x([0-9A-Fa-f]{2})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    .replace(/\\u([0-9A-Fa-f]{4})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )

const UniMap = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()

  const [selectedCityUniversities, setSelectedCityUniversities] = useState([])
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [drawerLoading, setDrawerLoading] = useState(false)

  const [selectedProvince, setSelectedProvince] = useState({ id: null, nameRaw: null })
  const [tooltip, setTooltip] = useState({ visible: false, name: '', x: 0, y: 0 })

  const mapContainerRef = useRef(null)
  const svgRef = useRef(null)

  useEffect(() => {
    const svgElement = mapContainerRef.current?.querySelector('svg')
    if (!svgElement) return

    svgRef.current = svgElement

    const handleMouseOver = (event) => {
      if (event.target.tagName !== 'path') return
      const provinceNameRaw = event.target.parentNode?.getAttribute('data-iladi')
      if (!provinceNameRaw) return
      const provinceName = decodeLabel(provinceNameRaw)
      setTooltip({
        visible: true,
        name: provinceName,
        x: event.pageX,
        y: event.pageY
      })
    }

    const handleMouseMove = (event) => {
      setTooltip((prev) =>
        prev.visible
          ? {
            ...prev,
            x: event.pageX,
            y: event.pageY
          }
          : prev
      )
    }

    const handleMouseOut = () => {
      setTooltip((prev) => ({ ...prev, visible: false }))
    }

    const handleClick = (event) => {
      if (event.target.tagName !== 'path') return
      const parentGroup = event.target.parentNode
      const provinceId = parentGroup?.getAttribute('id')
      const provinceNameRaw = parentGroup?.getAttribute('data-iladi')

      if (!provinceNameRaw) return

      const provinceName = decodeLabel(provinceNameRaw)

      setSelectedProvince({ id: provinceId, nameRaw: provinceNameRaw })

      // Directly fetch universities using the province name as search query
      openDrawerAndFetchUniversities(provinceName)
    }

    svgElement.addEventListener('mouseover', handleMouseOver)
    svgElement.addEventListener('mousemove', handleMouseMove)
    svgElement.addEventListener('mouseout', handleMouseOut)
    svgElement.addEventListener('click', handleClick)

    return () => {
      svgElement.removeEventListener('mouseover', handleMouseOver)
      svgElement.removeEventListener('mousemove', handleMouseMove)
      svgElement.removeEventListener('mouseout', handleMouseOut)
      svgElement.removeEventListener('click', handleClick)
    }
  }, [])

  const openDrawerAndFetchUniversities = (provinceName) => {
    setDrawerVisible(true)
    setDrawerLoading(true)

    dispatch(
      ApiCallActions.Get({
        url: 'universities',
        query: { search: provinceName, limit: 100 },
        onSuccess: ({ data }) => {
          setSelectedCityUniversities(data.data || [])
          setDrawerLoading(false)
        },
        onError: () => {
          setSelectedCityUniversities([])
          setDrawerLoading(false)
        }
      })
    )
  }

  // Highlight selected province
  useEffect(() => {
    if (!svgRef.current) return
    svgRef.current.querySelectorAll('g[data-selected="true"]').forEach((node) => node.removeAttribute('data-selected'))
    if (!selectedProvince.id) return
    const safeId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(selectedProvince.id) : selectedProvince.id
    const group = svgRef.current.querySelector(`g#${safeId}`)
    if (group) {
      group.setAttribute('data-selected', 'true')
    }
  }, [selectedProvince.id])

  const selectedProvinceLabel = useMemo(
    () => decodeLabel(selectedProvince.nameRaw || ''),
    [selectedProvince.nameRaw]
  )

  const handleDrawerClose = () => {
    setDrawerVisible(false)
    setSelectedCityUniversities([])
    setSelectedProvince({ id: null, nameRaw: null })
  }

  return (
    <section className="content uni-map-page">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {t('UNIMAP_PAGE_TITLE', 'Üniversite Haritası')}
              <small className="d-block">
                {t('UNIMAP_PAGE_SUBTITLE', 'Haritadan bir il seçerek üniversiteleri listeleyin.')}
              </small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i>
                  {t('COMMON_HOME')}
                </a>
              </li>
              <li className="breadcrumb-item active">
                {t('UNIMAP_BREADCRUMB', 'Üniversite Haritası')}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row clearfix">
          {/* Full width map */}
          <div className="col-lg-12">
            <div className="card">
              <div className="header">
                <h2>{t('UNIMAP_MAP_TITLE', 'Türkiye Haritası')}</h2>
                <small>{t('UNIMAP_MAP_HELPER', 'İllerin üzerine gelerek isimlerini görebilirsiniz.')}</small>
              </div>
              <div className="body">
                <div className="map-wrapper">
                  <div className="svg-turkiye-haritasi" ref={mapContainerRef}>
                    <TurkeyMapSvg />
                  </div>
                  <div
                    className={`il-isimleri ${tooltip.visible ? 'visible' : ''}`}
                    style={{ top: tooltip.y - 40, left: tooltip.x + 15 }}
                  >
                    {tooltip.visible && <div>{tooltip.name}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Universities Drawer */}
      <Drawer
        placement="right"
        width={500}
        onClose={handleDrawerClose}
        open={drawerVisible}
        destroyOnClose
        title={null}
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <div className="unimap-drawer-content">
          {/* Custom Header */}
          <div className="drawer-header-custom">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h3>{selectedProvinceLabel || t('UNIMAP_UNKNOWN_PROVINCE', 'İl')}</h3>
                <p>
                  {drawerLoading
                    ? t('COMMON_LOADING', 'Yükleniyor...')
                    : t('UNIMAP_LIST_TOTAL_COUNT', { count: selectedCityUniversities.length, defaultValue: '{{count}} Üniversite Listeleniyor' })
                  }
                </p>
              </div>
              <button
                onClick={handleDrawerClose}
                className="btn btn-link text-white p-0"
                style={{ fontSize: '24px', opacity: 0.8 }}
              >
                <i className="zmdi zmdi-close"></i>
              </button>
            </div>
          </div>

          <div className="p-4">
            {drawerLoading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="sr-only">{t('COMMON_LOADING', 'Yükleniyor...')}</span>
                </div>
              </div>
            ) : selectedCityUniversities.length === 0 ? (
              <div className="unimap-empty p-5 text-center">
                <div className="mb-4">
                  <i className="zmdi zmdi-city-alt" style={{ fontSize: '64px', color: '#cbd5e0' }}></i>
                </div>
                <h5 className="text-muted">{t('UNIMAP_EMPTY_STATE', 'Bu ilde kayıtlı üniversite bulunamadı.')}</h5>
              </div>
            ) : (
              <div className="university-list-container">
                {selectedCityUniversities.map((university) => (
                  <div
                    key={university.id}
                    className="university-card"
                    onClick={() => {
                      handleDrawerClose()
                      navigate(`/universities/${university.id}`, {
                        state: { from: location.pathname }
                      })
                    }}
                  >
                    {/* Logo Section */}
                    <div className="uni-logo">
                      {university.logoUrl ? (
                        <img
                          src={university.logoUrl}
                          alt={university.name}
                        />
                      ) : (
                        <i className="zmdi zmdi-graduation-cap"></i>
                      )}
                    </div>

                    {/* Content Section */}
                    <div className="uni-info">
                      <h4>{university.name}</h4>
                      <div className="uni-meta">
                        <span className="badge badge-light text-muted">
                          <i className="zmdi zmdi-pin mr-1"></i>
                          {decodeLabel(university.city?.cityName || university.province || '')}
                        </span>
                      </div>
                      <span className="uni-address">
                        {university.address || t('UNIMAP_NO_ADDRESS', 'Adres bilgisi yok')}
                      </span>
                    </div>

                    {/* Action */}
                    <div className="arrow-btn">
                      <i className="zmdi zmdi-chevron-right"></i>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </section>
  )
}

export default UniMap
